import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy } from "@angular/core";

/**
 * Máscara para escrever datas e horas à mão nos campos `mwlFlatpickr`.
 *
 * Escrever "12102026" sem barras fazia o flatpickr ler outra data (ia parar a
 * fevereiro). Aqui os separadores entram sozinhos à medida que se escreve, no
 * formato que o próprio campo mostra (`dateFormat` do flatpickr, ou
 * `altFormat` quando há `altInput`): "12102026" → "12/10/2026" num campo
 * `d/m/Y`, "1030" → "10:30" num `H:i`, e assim por diante.
 *
 * E não se espera por "sair do campo": assim que a data (ou a hora) fica
 * completa e válida, é aplicada ao flatpickr na hora — o calendário salta para
 * o dia e o formulário recebe o valor. Um utilizador que não visse nada
 * acontecer ao escrever achava que não tinha funcionado.
 *
 * Precisa de `[allowInput]="true"` no campo: sem isso o flatpickr põe o input
 * em só-leitura e não há nada para escrever.
 *
 * Com `altInput`, quem recebe as teclas é o input visível que o flatpickr
 * cria ao lado (o original fica escondido, com o valor no formato do
 * servidor). A diretiva vive no original, por isso liga os seus ouvintes ao
 * `altInput` logo que o flatpickr existe, e usa o `altFormat` para a máscara.
 *
 * Não é o `ngx-mask`: esse é um ControlValueAccessor, e o campo já tem o do
 * flatpickr — dois no mesmo input é erro do Angular. Esta diretiva só mexe no
 * valor do elemento e fala com o flatpickr; o formulário continua a ouvir o
 * flatpickr.
 *
 * Os dígitos escritos à mão passam por `beforeinput` e não por `input`: o
 * `angularx-flatpickr` lê o campo a cada `input` e empurrava para o formulário
 * datas a meio ("12/1" lia-se como 12 de janeiro). Tratando a tecla antes de
 * o browser a inserir, o formulário só recebe a data quando ela está inteira.
 * Colar e apagar continuam a passar por `input`, só para a formatação.
 *
 * O separador só entra quando já há um dígito a seguir ("12" fica "12", "121"
 * fica "12/1"): assim o backspace apaga o separador sem ele voltar a aparecer.
 *
 * Só formatos com campos de largura fixa (d, m, Y, y, H, i, S). Com outro
 * token (j, n, F, …) a diretiva fica inerte e o campo comporta-se como antes.
 */
type MaskField = "day" | "month" | "year" | "year2" | "hour" | "minute" | "second";

interface MaskGroup {
  field: MaskField;
  size: number;
}

interface Mask {
  groups: MaskGroup[];
  /** `separators[i]` fica entre `groups[i]` e `groups[i + 1]`. */
  separators: string[];
  total: number;
}

const TOKENS: { [token: string]: MaskGroup } = {
  d: { field: "day", size: 2 },
  m: { field: "month", size: 2 },
  Y: { field: "year", size: 4 },
  y: { field: "year2", size: 2 },
  H: { field: "hour", size: 2 },
  i: { field: "minute", size: 2 },
  S: { field: "second", size: 2 },
};

/** Letras que o flatpickr reconhece como tokens; as restantes são separadores. */
const FLATPICKR_TOKENS = /[dDjlJwWFmMnUyYZHhGiSsKu]/;

@Directive({
  selector: "input[appDateDigitsMask]",
})
export class DateDigitsMaskDirective implements AfterViewInit, OnDestroy {
  /** O input visível do flatpickr quando há `altInput`; senão o próprio host. */
  private alt: HTMLInputElement | null = null;
  private readonly altBeforeInput = (event: Event) =>
    this.handleBeforeInput(event as InputEvent, this.alt!, this.altFormat);
  private readonly altInput = () => this.handleInput(this.alt!, this.altFormat);

  constructor(private element: ElementRef<HTMLInputElement>) {}

  /**
   * O `angularx-flatpickr` cria a instância no seu próprio `ngAfterViewInit`;
   * a ordem entre diretivas do mesmo elemento não é garantida, daí o
   * `setTimeout`: ao correr, o flatpickr (e o `altInput`, se houver) já existe.
   */
  ngAfterViewInit(): void {
    setTimeout(() => this.attachAltInput());
  }

  ngOnDestroy(): void {
    if (!this.alt) return;
    this.alt.removeEventListener("beforeinput", this.altBeforeInput);
    this.alt.removeEventListener("input", this.altInput);
    this.alt = null;
  }

  private attachAltInput(): void {
    const alt = this.instance?.altInput as HTMLInputElement | undefined;
    if (!alt || this.alt) return;
    this.alt = alt;
    alt.addEventListener("beforeinput", this.altBeforeInput);
    alt.addEventListener("input", this.altInput);
  }

  /** Tecla a tecla: insere o dígito já formatado, sem deixar o browser inserir texto solto. */
  @HostListener("beforeinput", ["$event"])
  onBeforeInput(event: InputEvent): void {
    this.handleBeforeInput(event, this.element.nativeElement, this.dateFormat);
  }

  /** Colar, apagar, autocompletar: só normaliza a formatação. */
  @HostListener("input")
  onInput(): void {
    this.handleInput(this.element.nativeElement, this.dateFormat);
  }

  private handleBeforeInput(event: InputEvent, input: HTMLInputElement, format: string): void {
    if (event.inputType !== "insertText") return;
    const mask = this.mask(format);
    if (!mask) return;
    event.preventDefault();
    const typed = this.digitsOf(`${event.data || ""}`);
    if (!typed) return;

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const before = this.digitsOf(input.value.slice(0, start));
    const after = this.digitsOf(input.value.slice(end));

    const formatted = this.format(mask, before + typed + after);
    input.value = formatted;
    const caret = Math.min(this.format(mask, before + typed).length, formatted.length);
    input.setSelectionRange(caret, caret);

    this.applyIfComplete(mask, formatted, format);
  }

  private handleInput(input: HTMLInputElement, format: string): void {
    const mask = this.mask(format);
    if (!mask) return;
    const formatted = this.format(mask, input.value);
    if (formatted !== input.value) {
      input.value = formatted;
    }
    this.applyIfComplete(mask, formatted, format);
  }

  /* --------------------------------------------------------- flatpickr */

  private get instance(): any {
    return (this.element.nativeElement as any)._flatpickr || null;
  }

  private get dateFormat(): string {
    return `${this.instance?.config?.dateFormat || ""}`;
  }

  private get altFormat(): string {
    return `${this.instance?.config?.altFormat || ""}`;
  }

  /** Máscara derivada do formato do campo; `null` se o formato não for suportado. */
  private mask(format: string): Mask | null {
    if (!format) return null;

    const groups: MaskGroup[] = [];
    const separators: string[] = [];
    let separator = "";
    for (let i = 0; i < format.length; i++) {
      const char = format[i];
      if (char === "\\") {
        separator += format[++i] || "";
        continue;
      }
      const token = TOKENS[char];
      if (token) {
        if (groups.length) separators.push(separator);
        separator = "";
        groups.push(token);
        continue;
      }
      if (FLATPICKR_TOKENS.test(char)) return null;
      separator += char;
    }
    if (!groups.length) return null;
    return { groups, separators, total: groups.reduce((sum, group) => sum + group.size, 0) };
  }

  /**
   * Data ou hora inteira e válida → entrega-a ao flatpickr já. O `setDate`
   * com `triggerChange` dispara o `input` do flatpickr, que é o que o
   * `angularx-flatpickr` ouve para atualizar o formulário.
   *
   * Esse `input` volta a cair aqui (em `onInput`): a guarda abaixo vê que o
   * flatpickr já tem exatamente esta data e sai sem chamar `setDate` outra
   * vez — a cadeia tem sempre um só passo. Sem data selecionada (campo
   * limpo) não há nada para comparar e aplica-se logo; formatar uma data
   * inexistente rebentava no flatpickr.
   */
  private applyIfComplete(mask: Mask, value: string, format: string): void {
    const instance = this.instance;
    if (!instance || !this.isValid(mask, value, instance)) return;
    const selected = instance.selectedDates?.[0];
    if (selected && instance.formatDate(selected, format) === value) return;
    instance.setDate(value, true, format);
  }

  private isValid(mask: Mask, value: string, instance: any): boolean {
    if (this.digitsOf(value).length !== mask.total || this.format(mask, value) !== value) {
      return false;
    }

    const fields: Partial<Record<MaskField, number>> = {};
    let offset = 0;
    const digits = this.digitsOf(value);
    for (const group of mask.groups) {
      fields[group.field] = Number(digits.slice(offset, offset + group.size));
      offset += group.size;
    }

    if (fields.month !== undefined && (fields.month < 1 || fields.month > 12)) return false;
    if (fields.day !== undefined && (fields.day < 1 || fields.day > 31)) return false;
    if (fields.hour !== undefined && fields.hour > 23) return false;
    if (fields.minute !== undefined && fields.minute > 59) return false;
    if (fields.second !== undefined && fields.second > 59) return false;
    if (fields.year !== undefined && fields.year < 1900) return false;

    // Dia, mês e ano todos presentes: tem de ser um dia que exista
    // (31/02 não passa) e caber no min/max do campo, senão o flatpickr
    // limpava o texto escrito.
    if (fields.year !== undefined && fields.month !== undefined && fields.day !== undefined) {
      const date = new Date(
        fields.year,
        fields.month - 1,
        fields.day,
        fields.hour || 0,
        fields.minute || 0,
        fields.second || 0
      );
      if (
        date.getFullYear() !== fields.year ||
        date.getMonth() !== fields.month - 1 ||
        date.getDate() !== fields.day
      ) {
        return false;
      }
      const min = instance.config?.minDate ? new Date(instance.config.minDate) : null;
      const max = instance.config?.maxDate ? new Date(instance.config.maxDate) : null;
      if (min) {
        min.setHours(0, 0, 0, 0);
        if (date.getTime() < min.getTime()) return false;
      }
      if (max) {
        max.setHours(23, 59, 59, 999);
        if (date.getTime() > max.getTime()) return false;
      }
    }
    return true;
  }

  /* ------------------------------------------------------------- texto */

  private digitsOf(value: string): string {
    return `${value || ""}`.replace(/\D/g, "");
  }

  private format(mask: Mask, value: string): string {
    const digits = this.digitsOf(value).slice(0, mask.total);
    let result = "";
    let offset = 0;
    for (let i = 0; i < mask.groups.length && offset < digits.length; i++) {
      if (i > 0) result += mask.separators[i - 1];
      result += digits.slice(offset, offset + mask.groups[i].size);
      offset += mask.groups[i].size;
    }
    return result;
  }
}
